import React from 'react';

/**
 * Componente unificado para mostrar jugadores/equipos en diferentes deportes
 * Se adapta automáticamente según el tipo de deporte (general o tenis)
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.type - Tipo de deporte ('general' o 'tennis')
 * @param {string} props.imagen - URL de la imagen del equipo/jugador
 * @param {string} props.name - Nombre del equipo o jugador
 * @param {string} props.url - URL base para imágenes por defecto (solo general)
 * @param {string|number} props.seed - Ranking/seed del jugador (solo tenis)
 * @param {Array} props.flags - Array de códigos de países disponibles (solo tenis)
 * 
 * @returns {JSX.Element} Contenedor con información del jugador/equipo
 */
export default function Player({ 
  type = 'general', 
  imagen, 
  name, 
  url, 
  seed, 
  flags = [] 
}) {

  /**
   * Renderiza la imagen según el tipo de deporte
   */
  const renderImage = () => {
    if (type === 'tennis') {
      // Lógica para tenis - bandera del país
      const hasFlag = String(imagen).trim().length !== 0 && flags.includes(imagen);
      
      if (hasFlag) {
        return (
          <div
            className={`flag-tenis bandera-${imagen}`}
            role="img"
            aria-label={`Bandera de ${imagen}`}
            title={`País: ${imagen}`}
          />
        );
      }

      return (
        <div
          className="img-null"
          role="img"
          aria-label="Bandera no disponible"
          title="Bandera no disponible"
        />
      );
    }

    // Lógica para deportes generales - logo del equipo
    const imageClasses = String(imagen).trim().length === 0 ? "opacity" : "";
    const imageSrc = String(imagen).trim().length === 0 ? `${url}shield.png` : imagen;

    return (
      <img 
        loading="lazy" 
        src={imageSrc}
        className={imageClasses}
        alt={name || "Equipo"} 
        title={name}
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
    );
  };

  /**
   * Renderiza el seed/ranking para tenis
   */
  const renderSeed = () => {
    if (type === 'tennis' && seed) {
      return (
        <p className="position-tenis" title={`Ranking: ${seed}`}>
          {seed}
        </p>
      );
    }
    return null;
  };

  /**
   * Determina las clases CSS del contenedor según el tipo
   */
  const containerClass = type === 'tennis' 
    ? 'card-body-left-one-tenis' 
    : 'card-body-left-one';

  /**
   * Determina las clases CSS del nombre según el tipo
   */
  const nameClass = type === 'tennis' 
    ? 'name-player' 
    : 'name-team';

  /**
   * Procesa el nombre para tenis (trunca si es mayor a 20 caracteres)
   */
  const displayName = type === 'tennis' && name && name.length > 20 
    ? `${name.substring(0, 20)}...` 
    : name;

  return (
    <div className={containerClass}>
      {/* Imagen/Bandera */}
      {renderImage()}
      
      {/* Seed/Ranking (solo tenis) */}
      {renderSeed()}

      {/* Nombre del jugador/equipo */}
      <p className={nameClass} title={name}>
        {displayName}
      </p>
    </div>
  );
}